export type EnquiryDraft = {
  customer_id: string;
  customer_revision: string;
  vehicle_id: string;
  vehicle_revision: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  make: string;
  model: string;
  fabrication_year: string;
  colour: string;
  registration_number: string;
  vin: string;
  channel: string;
  channel_identity: string;
  body: string;
  service_details: string;
  photo_ids: string[];
  files: File[];
};
type ProfileRevision = { id: string; revision: string };

export function reconcileEnquiryProfileRevisions(
  draft: EnquiryDraft,
  customers: ProfileRevision[],
  vehicles: ProfileRevision[],
): EnquiryDraft {
  const customerRevision =
    customers.find((profile) => profile.id === draft.customer_id)?.revision ||
    draft.customer_revision;
  const vehicleRevision =
    vehicles.find((profile) => profile.id === draft.vehicle_id)?.revision ||
    draft.vehicle_revision;
  if (
    customerRevision === draft.customer_revision &&
    vehicleRevision === draft.vehicle_revision
  )
    return draft;
  return {
    ...draft,
    customer_revision: customerRevision,
    vehicle_revision: vehicleRevision,
  };
}

const prefix = "garageclerk:enquiry:";
// The same tab may change routed components after customer association. Keep
// browser File objects in memory only; a hard reload deliberately drops them.
const liveDrafts = new Map<
  string,
  { revision: string; expires: number; values: EnquiryDraft }
>();
const textFields = [
  "customer_id",
  "customer_revision",
  "vehicle_id",
  "vehicle_revision",
  "name",
  "email",
  "phone",
  "address",
  "make",
  "model",
  "fabrication_year",
  "colour",
  "registration_number",
  "vin",
  "channel",
  "channel_identity",
  "body",
  "service_details",
] as const;

export function restoreEnquiryDraft(
  key: string,
  revision: string,
  fallback: EnquiryDraft,
  continueNewEnquiry = false,
): EnquiryDraft {
  try {
    const live = liveDrafts.get(key);
    const raw = live ? null : sessionStorage.getItem(prefix + key);
    if (!live && (!raw || raw.length > 64 * 1024)) return fallback;
    const saved = live || JSON.parse(raw!);
    const continuing = continueNewEnquiry && saved.revision === "new";
    if (
      (saved.revision !== revision && !continuing) ||
      saved.expires <= Date.now() ||
      !saved.values
    )
      return fallback;
    const values = { ...fallback };
    for (const field of textFields)
      if (typeof saved.values[field] === "string")
        values[field] = saved.values[field];
    // Only live browser files survive a routed continuation; serialized input
    // cannot claim uploads or change the server's retained attachment choices.
    if (live) values.files = live.values.files;
    if (continuing) liveDrafts.delete(key);
    return values;
  } catch {
    return fallback;
  }
}

export function rememberEnquiryDraft(
  key: string,
  revision: string,
  draft: EnquiryDraft,
) {
  const expires = Date.now() + 24 * 60 * 60 * 1000;
  for (const [old, saved] of liveDrafts)
    if (saved.expires <= Date.now()) liveDrafts.delete(old);
  if (!liveDrafts.has(key) && liveDrafts.size >= 20)
    liveDrafts.delete(liveDrafts.keys().next().value!);
  liveDrafts.set(key, {
    revision,
    expires,
    values: { ...draft, files: [...draft.files] },
  });
  try {
    const values = Object.fromEntries(
      textFields.map((field) => [field, draft[field]]),
    );
    const raw = JSON.stringify({
      revision,
      expires,
      values,
    });
    if (raw.length > 64 * 1024) return;
    const keys = Object.keys(sessionStorage).filter(
      (item) => item.startsWith(prefix) && item !== prefix + key,
    );
    for (const old of keys.slice(0, Math.max(0, keys.length - 19)))
      sessionStorage.removeItem(old);
    sessionStorage.setItem(prefix + key, raw);
  } catch {
    // A blocked/full browser store does not prevent saving the enquiry.
  }
}

export function forgetEnquiryDraft(key: string) {
  liveDrafts.delete(key);
  try {
    sessionStorage.removeItem(prefix + key);
  } catch {
    /* Browser storage may be blocked. */
  }
}
